import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  RefreshCw, Search, User, Phone, Mail, Calendar, Clock, AlertTriangle,
  CheckCircle, XCircle, ArrowRight, ChevronRight, Plus, FileText, DollarSign
} from 'lucide-react';

const STATUSES = [
  { key: 'NEW', label: 'New', color: 'bg-blue-500', bgLight: 'bg-blue-500/5' },
  { key: 'REVIEW', label: 'Reviewing', color: 'bg-purple-500', bgLight: 'bg-purple-500/5' },
  { key: 'WAITING_DOCS', label: 'Waiting Docs', color: 'bg-amber-500', bgLight: 'bg-amber-500/5' },
  { key: 'ASSESSMENT', label: 'Assessment', color: 'bg-cyan-500', bgLight: 'bg-cyan-500/5' },
  { key: 'APPROVED', label: 'Approved', color: 'bg-green-500', bgLight: 'bg-green-500/5' },
  { key: 'REJECTED', label: 'Rejected', color: 'bg-red-500', bgLight: 'bg-red-500/5' },
  { key: 'CONVERTED', label: 'Converted', color: 'bg-emerald-500', bgLight: 'bg-emerald-500/5' },
];

const urgencyColors = {
  LOW: 'bg-gray-500/10 text-gray-600',
  MEDIUM: 'bg-amber-500/10 text-amber-600',
  HIGH: 'bg-red-500/10 text-red-600',
};

function ReferralCard({ referral, onStatusChange, onConvert }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Card 
        className="cursor-pointer hover:border-primary/30 transition-colors mb-3"
        onClick={() => setShowDetails(true)}
        data-testid={`referral-${referral.referralNo}`}
      >
        <CardContent className="p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-medium text-sm">{referral.participantName}</p>
              <p className="text-xs text-muted-foreground">{referral.referralNo}</p>
            </div>
            <Badge variant="outline" className={urgencyColors[referral.urgencyLevel] || ''}>
              {referral.urgencyLevel}
            </Badge>
          </div>
          <div className="space-y-1 text-xs text-muted-foreground">
            {referral.phone && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {referral.phone}
              </div>
            )}
            {referral.fundingType && (
              <div className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {referral.fundingType}
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(referral.createdAt).toLocaleDateString('en-AU')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {referral.participantName}
            </DialogTitle>
            <DialogDescription>{referral.referralNo}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="font-medium">{referral.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium">{referral.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Funding Type</p>
                <p className="font-medium">{referral.fundingType || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">NDIS Number</p>
                <p className="font-medium">{referral.ndisNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Location</p>
                <p className="font-medium">{referral.locationPreference || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Urgency</p>
                <Badge variant="outline" className={urgencyColors[referral.urgencyLevel] || ''}>
                  {referral.urgencyLevel}
                </Badge>
              </div>
            </div>
            {referral.supportNeeds && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Support Needs</p>
                <p className="text-sm bg-muted/30 p-3 rounded-lg">{referral.supportNeeds}</p>
              </div>
            )}
            {referral.notes && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Notes</p>
                <p className="text-sm bg-muted/30 p-3 rounded-lg">{referral.notes}</p>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Created: {new Date(referral.createdAt).toLocaleString('en-AU')}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {referral.status !== 'REJECTED' && referral.status !== 'CONVERTED' && (
              <>
                {referral.status === 'APPROVED' && (
                  <Button onClick={() => { onConvert(referral); setShowDetails(false); }} className="bg-emerald-500 hover:bg-emerald-600">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Convert to Participant
                  </Button>
                )}
                {referral.status !== 'APPROVED' && (
                  <Button onClick={() => { onStatusChange(referral, 'next'); setShowDetails(false); }}>
                    Move to Next Stage
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
                <Button variant="destructive" onClick={() => { onStatusChange(referral, 'REJECTED'); setShowDetails(false); }}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BoardColumn({ status, referrals, onStatusChange, onConvert, loading }) {
  const count = referrals.length;

  return (
    <div className={`flex-1 min-w-[280px] rounded-lg border ${status.bgLight}`}>
      <div className="p-3 border-b bg-background/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-2.5 w-2.5 rounded-full ${status.color}`} />
            <h3 className="font-medium text-sm">{status.label}</h3>
          </div>
          <Badge variant="secondary" className="text-xs">{count}</Badge>
        </div>
      </div>
      <div className="p-3 min-h-[300px] max-h-[calc(100vh-300px)] overflow-y-auto">
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <Card key={i}>
                <CardContent className="p-3">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-3 w-16 mb-3" />
                  <Skeleton className="h-3 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No referrals
          </div>
        ) : (
          referrals.map(ref => (
            <ReferralCard 
              key={ref.id} 
              referral={ref} 
              onStatusChange={onStatusChange}
              onConvert={onConvert}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function ReferralDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const res = await api.get('/referrals?pageSize=100');
      setReferrals(res.data.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const getNextStatus = (currentStatus) => {
    const statusOrder = ['NEW', 'REVIEW', 'WAITING_DOCS', 'ASSESSMENT', 'APPROVED'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex >= statusOrder.length - 1) return null;
    return statusOrder[currentIndex + 1];
  };

  const handleStatusChange = async (referral, action) => {
    try {
      let newStatus;
      if (action === 'next') {
        newStatus = getNextStatus(referral.status);
        if (!newStatus) {
          toast.error('Cannot advance further');
          return;
        }
      } else if (action === 'REJECTED') {
        await api.post(`/referrals/${referral.id}/reject`, { reason: 'Rejected via dashboard' });
        toast.success('Referral rejected');
        fetchReferrals();
        return;
      } else {
        newStatus = action;
      }

      await api.put(`/referrals/${referral.id}`, { status: newStatus });
      toast.success(`Referral moved to ${newStatus}`);
      fetchReferrals();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleConvert = async (referral) => {
    try {
      const res = await api.post(`/referrals/${referral.id}/convert`);
      toast.success(`Referral converted to participant`);
      fetchReferrals();
      // Optionally navigate to the new participant
      if (res.data.data?.participant?.id) {
        navigate(`/participants/${res.data.data.participant.id}`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Filter referrals
  const filteredReferrals = referrals.filter(ref => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ref.participantName?.toLowerCase().includes(query) ||
      ref.referralNo?.toLowerCase().includes(query) ||
      ref.phone?.toLowerCase().includes(query) ||
      ref.email?.toLowerCase().includes(query)
    );
  });

  // Group by status
  const referralsByStatus = STATUSES.reduce((acc, status) => {
    acc[status.key] = filteredReferrals.filter(r => r.status === status.key);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Referral Pipeline</h1>
          <p className="text-muted-foreground">Manage incoming referrals</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchReferrals} disabled={loading} data-testid="refresh-referrals">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search referrals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-referrals"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {STATUSES.map(status => (
          <Card key={status.key} className={status.bgLight}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{referralsByStatus[status.key]?.length || 0}</p>
              <p className="text-xs text-muted-foreground">{status.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STATUSES.map(status => (
          <BoardColumn
            key={status.key}
            status={status}
            referrals={referralsByStatus[status.key] || []}
            onStatusChange={handleStatusChange}
            onConvert={handleConvert}
            loading={loading}
          />
        ))}
      </div>
    </div>
  );
}
